import * as valibot from 'valibot';
import { djb2 } from './djb2';
import type { StorageLike } from './storage';

/** LocalStorage key for a successful unlock (30-day TTL). */
export const GATE_UNLOCK_KEY = 'app-unlock-v3';

/** Days the unlock is kept in localStorage before requiring re-entry. */
export const GATE_UNLOCK_TTL_DAYS = 30;

/**
 * Vite-inlined build-time password. Missing (`null`/`undefined`) falls back
 * to `'lol'`; an explicit empty string is respected — set `VITE_GATE_PASSWORD`
 * to a non-empty value to rotate for prod.
 */
const GATE_PASSWORD = import.meta.env.VITE_GATE_PASSWORD ?? 'lol';

/**
 * Precomputed hash the {@link Gate} component checks input against. Rotate by
 * setting `VITE_GATE_PASSWORD` at build time (invalidates existing unlocks).
 * Not a security boundary — every API route is unauthenticated.
 */
export const PASSWORD_HASH = djb2(GATE_PASSWORD);

/** Hostnames that skip the gate — local dev has no need to log in. */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]', '::1']);

/** True when the app is served from a local development host. */
export function isLocalHost(hostname: string): boolean {
  return LOCAL_HOSTS.has(hostname);
}

/** Runtime schema for the persisted unlock payload; shape only, freshness is a separate check. */
const UnlockSchema = valibot.object({
  hash: valibot.number(),
  expires: valibot.number(),
});

/** True when `record` is a well-formed, non-expired unlock for {@link PASSWORD_HASH}. Used by {@link hasValidUnlock}. */
export function isUnlockValid(record: unknown, nowMs: number): boolean {
  if (!valibot.is(UnlockSchema, record)) return false;
  return record.hash === PASSWORD_HASH && record.expires > nowMs;
}

/**
 * Loads the persisted unlock written by {@link saveUnlock}; returns true iff
 * {@link isUnlockValid} accepts it. Silently swallows storage / parse failures.
 */
export function hasValidUnlock(storage: StorageLike, nowMs: number): boolean {
  try {
    const rawJson = storage.getItem(GATE_UNLOCK_KEY);
    if (!rawJson) return false;
    const parsed: unknown = JSON.parse(rawJson);
    return isUnlockValid(parsed, nowMs);
  } catch {
    return false;
  }
}

/** Shape of the persisted unlock payload written by {@link saveUnlock}. */
interface UnlockRecord {
  hash: number;
  expires: number;
}

/**
 * Persists a fresh unlock record valid for {@link GATE_UNLOCK_TTL_DAYS} days.
 * Silently swallows quota / private-mode failures. Read via {@link hasValidUnlock}.
 */
export function saveUnlock(storage: StorageLike, nowMs: number): void {
  const record: UnlockRecord = {
    hash: PASSWORD_HASH,
    expires: nowMs + GATE_UNLOCK_TTL_DAYS * 86_400_000,
  };
  try {
    storage.setItem(GATE_UNLOCK_KEY, JSON.stringify(record));
  } catch {
    /* quota / private mode */
  }
}
